import { storePayment } from './store';
import {
	createSubscription,
	upgradeSubscription,
	downgradeSubscription,
	cancelSubscription,
	setDefaultPaymentMethod,
	createPaymentIntent
} from './ajax';
import { PAYMENT_ACTIONS, POPUP_IDS } from './constants.js';
import { displayPopup, confirmSetup } from './utilities.js';

/**
 * Handle payment method tab switching
 */
function handlePaymentMethodTabs() {
	const tabBtns = document.querySelectorAll('.method-payment-tab .method-payment-btn');
	const tabContents = document.querySelectorAll('.method-payment-tab-content');

	tabBtns.forEach(btn => {
		btn.addEventListener('click', () => {
			const tab = btn.dataset.tab;

			// Toggle tab buttons
			tabBtns.forEach(b => b.classList.remove('active'));
			btn.classList.add('active');

			// Toggle tab contents
			tabContents.forEach(content => {
				if (content.dataset.tabContent === tab) {
					content.classList.remove('hidden');
				} else {
					content.classList.add('hidden');
				}
			});
		});
	});
}

/**
 * Handle payment method selection
 * Initializes default payment method and manages click events
 */
function handleChoosePaymentMethod() {
	handlePaymentMethodTabs();

	const methods = document.querySelectorAll('.method-payment-card');
	const payBtn = document.getElementById('confirm-pay');

	methods.forEach(method => {
		const btn = method.querySelector('.choose-btn');

		// Initialize default payment method on load
		if (btn && btn.classList.contains('active')) {
			storePayment.setPaymentMethod(method.dataset.id);
		}

		// Handle payment method selection
		method.addEventListener('click', () => {
			// Deselect all methods across all tabs
			methods.forEach(m => {
				const chooseBtn = m.querySelector('.choose-btn');
				if (chooseBtn) {
					chooseBtn.classList.remove('active');
				}
			});

			btn.classList.add('active');
			if (payBtn) payBtn.disabled = false;
			storePayment.setPaymentMethod(method.dataset.id);
		});
	});
}

/**
 * Handle downgrade subscription action
 */
function handleDowngrade() {
	const btn = document.getElementById('downgrade-subscription');
	if (!btn) return;

	btn.addEventListener('click', async () => {
		const subscription_id = btn.dataset.subscriptionId;
		const nonce = btn.dataset.nonce;
		btn.disabled = true;
		btn.innerText = wpcore.direct_t({ en: 'Handling...', fr: 'Traitement...' });

		const response = await downgradeSubscription(storePayment.getPaymentMethod(), subscription_id, nonce);

		if (!response || response?.error) {
			btn.disabled = false;
			btn.innerText = wpcore.direct_t({ en: 'Downgrade Subscription', fr: 'R\u00e9trograder l\'abonnement' });
			wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
			return;
		}

		btn.innerText = wpcore.direct_t({ en: 'Downgrade completed', fr: 'R\u00e9trogradation termin\u00e9e' });
		wpcore.closePopup(POPUP_IDS.CONFIRM_DOWNGRADE);
		wpcore.showPopup(POPUP_IDS.DOWNGRADE_SUCCESS);
	});
}

/**
 * Handle payment using existing card
 * Supports create, upgrade, and downgrade actions
 */
function handlePayUsingExitsCard() {
	const btn = document.getElementById('confirm-pay');
	if (!btn) return;

	btn.addEventListener('click', async () => {
		const action = btn.dataset.action;
		const subscription_id = btn.dataset.subscriptionId;
		const nonce = btn.dataset.nonce;
		let response = null;

		btn.disabled = true;
		btn.innerText = wpcore.direct_t({ en: 'Handling...', fr: 'Traitement...' });

		// Execute appropriate action
		if (action === PAYMENT_ACTIONS.PAYMENT_SUBSCRIPTION) {
			response = await createSubscription(storePayment.getPaymentMethod(), subscription_id, nonce);
		} else if (action === PAYMENT_ACTIONS.UPGRADE) {
			response = await upgradeSubscription(storePayment.getPaymentMethod(), subscription_id, nonce);
		} else if (action === PAYMENT_ACTIONS.PAYMENT_SESSION) {
			const draftBookingId = btn.dataset.draftBookingId;
			response = await createPaymentIntent(storePayment.getPaymentMethod(), nonce, { draft_booking_id: draftBookingId });
		}

		displayPopup(response, btn, action);
	});
}

/**
 * Handle cancel subscription action
 */
function handleCancelSubscription() {
	const btn = document.getElementById('btn-cancel-subscription');
	if (!btn) return;

	btn.addEventListener('click', async () => {
		const nonce = btn.dataset.nonce;
		const response = await cancelSubscription(nonce);

		if (!response || response?.error) {
			wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
			return;
		}

		wpcore.closePopup('cancel-subscription');
		wpcore.showPopup('cancel-subscription-success');
	});
}

/**
 * Handle set default payment method
 */
function handleSetDefaultPaymentMethod() {
	const buttons = document.querySelectorAll('.btn-set-default');
	if (!buttons.length) return;

	buttons.forEach((btn) => {
		btn.addEventListener('click', async () => {
			const paymentMethodId = btn.dataset.paymentMethodId;
			const response = await setDefaultPaymentMethod(paymentMethodId);

			if (!response || response?.error) {
				alert(wpcore.direct_t({ en: 'Failed to set default payment method', fr: '\u00c9chec de la d\u00e9finition du moyen de paiement par d\u00e9faut' }));
				return;
			}

			window.location.reload();
		});
	});
}



export function handlePayment(stripe, elements) {
	const checkoutForms = document.querySelectorAll('[checkout-form]');
	checkoutForms.forEach((form) => {
		form.addEventListener('submit', async (e) => {
			e.preventDefault();
			const submitBtn = form.querySelector('button[type="submit"]');
			const nonce = form.querySelector('input[name="nonce"]').value;
			const action = submitBtn.dataset.action;
			submitBtn.disabled = true;
			submitBtn.innerText = wpcore.direct_t({ en: 'Handling payment...', fr: 'Traitement du paiement...' });

			// Always create fresh PaymentMethod from form elements.
			// This ensures switching cards after a failed attempt uses the new card.
			await confirmSetup(stripe, elements, submitBtn);

			// If createPaymentMethod failed, stop here
			if (!storePayment.getPaymentMethod()) {
				return;
			}

			let response = null;

			if (action === PAYMENT_ACTIONS.PAYMENT_SUBSCRIPTION) {
				const subscriptionId = form.querySelector('input[name="subscription_id"]').value;
				response = await createSubscription(storePayment.getPaymentMethod(), subscriptionId, nonce);
			} else if (action === PAYMENT_ACTIONS.UPGRADE) {
				const subscriptionId = form.querySelector('input[name="subscription_id"]').value;
				response = await upgradeSubscription(storePayment.getPaymentMethod(), subscriptionId, nonce);
			} else if (action === PAYMENT_ACTIONS.PAYMENT_SESSION) {
				const draftBookingId = form.querySelector('input[name="draft_booking_id"]').value;
				response = await createPaymentIntent(storePayment.getPaymentMethod(), nonce, { draft_booking_id: draftBookingId });
			}

			displayPopup(response, submitBtn, action);
		});
	});
}

export function handleClosePopupSuccess() {
	document.addEventListener('popup-closed', (event) => {
		const { target } = event.detail;
		if (target === 'payment-subscription-success' || target === 'upgrade-subscription-success' || target === 'downgrade-subscription-success') {
			window.location.href = '/students';
		} else if (target == 'booking-success') {
			window.location.href = '/students/my-sessions';
		} else if (target === 'cancel-subscription-success') {
			window.location.reload();
		}
	});
}

/**
 * Initialize all payment handlers
 */
export function initHandler() {
	handlePayUsingExitsCard();
	handleChoosePaymentMethod();
	handleDowngrade();
	handleCancelSubscription();
	handleSetDefaultPaymentMethod();
	handleClosePopupSuccess();
}
